/**
 * FirstLoginJunction — the two-option chooser shown to a freshly-
 * created shop's first user after OnboardShop.
 *
 *   Option 1: Import Shop History  (rendered ONLY when
 *             `importEnabledForInternal` is true for the viewing user;
 *             real shops never see a clickable Import path until the
 *             end-to-end import workflow is functional)
 *   Option 2: Manual Quick Setup → swaps to <LeanSetupWizard prefillSource="manual" />
 *
 * Strict no-fake-UX rule:
 *   - The Import card is not rendered at all when the flag is off. No
 *     greyed-out / "Coming soon" placeholder for real users.
 *   - When only the Manual card is rendered, it centers gracefully and
 *     does not imply something else is missing.
 *
 * Slice 2.5 wiring:
 *   - Import button navigates to /onboard/setup/import — the real
 *     HistoricalImportDialog page — instead of the Slice 1
 *     placeholder fallback that routed internal testers to Manual.
 *   - Manual button remains unchanged (renders LeanSetupWizard
 *     in-place as before).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Sparkles, Wrench, Upload } from 'lucide-react';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { LeanSetupWizard } from './LeanSetupWizard';

export function FirstLoginJunction() {
  const navigate = useNavigate();
  const importEnabled = useFeatureFlag('importEnabledForInternal');
  const [choice, setChoice] = useState<'chooser' | 'manual'>('chooser');

  if (choice === 'manual') {
    return <LeanSetupWizard prefillSource="manual" />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4" data-testid="first-login-junction">
      <div className="w-full max-w-4xl space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">Welcome to OptiBay</h1>
          <p className="text-muted-foreground">Let's get your shop set up.</p>
        </div>

        <div className={`grid gap-4 ${importEnabled ? 'md:grid-cols-2' : 'md:max-w-md md:mx-auto'}`}>
          {importEnabled && (
            <Card className="cursor-pointer hover:border-primary transition" data-testid="first-login-import-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Import Shop History
                </CardTitle>
                <CardDescription>
                  Upload a CSV export from your previous system. OptiBay will analyze it and prefill your setup. (Other DMS formats are coming soon.)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  className="w-full gap-2"
                  onClick={() => navigate('/onboard/setup/import')}
                  data-testid="first-login-import-button"
                >
                  <Sparkles className="h-4 w-4" /> Import history
                </Button>
              </CardContent>
            </Card>
          )}
          <Card className="cursor-pointer hover:border-primary transition" data-testid="first-login-manual-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                Manual Quick Setup
              </CardTitle>
              <CardDescription>
                Three quick pages — shop basics, your team, and a starter floor / parking layout. Takes about 5 minutes. You can refine everything later in Settings and the Birdseye editor.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" onClick={() => setChoice('manual')} data-testid="first-login-manual-button">
                Start setup
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
